import PropTypes from 'prop-types';
import React, { useState } from 'react';
import * as Yup from 'yup';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Heading from '../../components/Heading/Heading';
import FormOverlap from '../../components/FormOverlap/FormOverlap';
import Error from '../../components/Error/Error';
import states from '../../data/states';
import submitCommercialLendingApplication from '../../services/submitCommercialLendingApplication';
import useGTM from '../../hooks/useGTM';
import createGTMClass from '../../util/createGTMClass';
import CTACardv2 from '../../components/CTACardv2/CTACardv2';
import useIsMobile from '../../hooks/useIsMobile';
import theme from '../../styles/theme';
import Styles from './CommercialLendingApplication.styled';

const minDate = new Date();
minDate.setMonth(minDate.getMonth() + 1);
const stateNames = states.reduce(
  (_states, { value }) => [..._states, value],
  []
);
const fields = [
  {
    title: 'Company Information',
    rows: [
      [
        {
          name: 'companyName',
          label: 'Company name',
          type: 'text',
          validationSchema: Yup.string()
            .max(100, 'Company name is too long.')
            .matches(
              /^[.-/&!_',: A-Za-z0-9]+$/,
              "You've entered a symbol that isn't valid."
            ),
        },
      ],
      [
        {
          name: 'companyAddress',
          label: 'Company address',
          type: 'text',
          validationSchema: Yup.string()
            .max(80, 'Company address is too long.')
            .matches(
              /^[A-Za-z0-9 ',-]+$/,
              "You've entered a symbol that isn't valid."
            ),
        },
      ],
      [
        {
          name: 'companyCity',
          label: 'City',
          type: 'text',
          className: 'mobile-full tablet-full desktop-third',
          validationSchema: Yup.string()
            .max(50, 'City is too long.')
            .matches(
              /^[A-Za-z0-9 -]+$/,
              "You've entered a symbol that isn't valid."
            ),
        },
        {
          name: 'companyState',
          label: 'State',
          type: 'dropdown',
          values: states.map(({ label, value }) => ({
            label,
            value,
          })),
          showFormatted: false,
          className: 'mobile-half tablet-half desktop-third',
          validationSchema: Yup.string().matches(
            new RegExp(stateNames.join('|')),
            'State must be selected from the list.'
          ),
        },
        {
          name: 'companyZip',
          label: 'Zip',
          type: 'text',
          className: 'mobile-half tablet-half desktop-third',
          validationSchema: Yup.string().matches(
            /^\d{5}(-?\d{4})?$/,
            'Zip must be formatted either XXXXX or XXXXX-XXXX.'
          ),
        },
      ],
      [
        {
          name: 'yearsInBusiness',
          label: 'Years in business',
          type: 'text',
          className: 'mobile-full tablet-full',
          validationSchema: Yup.string()
            .max(100, 'Years in business is too long.')
            .matches(
              /^[A-Za-z0-9 .]+$/,
              "You've entered a symbol that isn't valid."
            ),
        },
        {
          name: 'businessType',
          label: 'Business type',
          type: 'text',
          className: 'mobile-full tablet-full',
          validationSchema: Yup.string()
            .max(50, 'Business type is too long.')
            .matches(
              /^[A-Za-z0-9 /&!._',]+$/,
              "You've entered a symbol that isn't valid."
            ),
        },
      ],
      [
        {
          name: 'annualRevenue',
          label: 'Annual revenue',
          type: 'text',
          validationSchema: Yup.string()
            .max(50, 'Annual revenue is too long.')
            .matches(
              /^[A-Za-z0-9 $.,]+$/,
              "You've entered a symbol that isn't valid."
            ),
        },
      ],
      [
        {
          name: 'phoneNumber',
          label: 'Phone number',
          type: 'text',
          oninput:
            "this.value = this.value.replace(/[^0-9.]/g, '').replace(/(..*)./g, '$1');",
          validationSchema: Yup.string()
            .matches(
              /^(\+?( |-|\.)?\d{1,2}( |-|\.)?)?(\(?\d{3}\)?|\d{3})( |-|\.)?(\d{3}( |-|\.)?\d{4})+$/,
              'Phone must be formatted correctly ###-###-####.'
            )
            .required('Phone number is required.'),
          formatter: 'phone',
        },
      ],
    ],
  },
  {
    title: 'Borrower Information',
    rows: [
      [
        {
          name: 'borrowerName',
          label: 'Borrower name',
          type: 'text',
          validationSchema: Yup.string()
            .max(100, 'Borrower name is too long')
            .matches(
              /^[A-Za-z0-9 &!._'-]+$/,
              "You've entered a symbol that isn't valid."
            )
            .required('Borrower name is required.'),
        },
      ],
      [
        {
          name: 'percentOwnership',
          label: '% Ownership',
          type: 'text',
          validationSchema: Yup.string()
            .max(50, '% Ownership is too long.')
            .matches(
              /^[A-Za-z0-9 ]+$/,
              "You've entered a symbol that isn't valid."
            ),
        },
      ],
      [
        {
          name: 'borrowerEmail',
          label: 'Email Address',
          type: 'text',
          validationSchema: Yup.string()
            .max(254, 'Email address too long.')
            .matches(
              /^\S{1,}@\S{1,}\.\S{1,}$/,
              'You entered an invalid email address.'
            )
            .required('Email address is required.'),
        },
      ],
      [
        {
          name: 'borrowerAddress',
          label: 'Borrower address',
          type: 'text',
          validationSchema: Yup.string()
            .max(80, 'Borrower address is too long.')
            .matches(
              /^[A-Za-z0-9 ',-]+$/,
              "You've entered a symbol that isn't valid."
            ),
        },
      ],
      [
        {
          name: 'borrowerCity',
          label: 'City',
          type: 'text',
          className: 'mobile-full tablet-full desktop-third',
          validationSchema: Yup.string()
            .max(50, 'City is too long.')
            .matches(
              /^[A-Za-z0-9 -]+$/,
              "You've entered a symbol that isn't valid."
            ),
        },
        {
          name: 'borrowerState',
          label: 'State',
          type: 'dropdown',
          values: states.map(({ label, value }) => ({
            label,
            value,
          })),
          showFormatted: false,
          className: 'mobile-half tablet-half desktop-third',
          validationSchema: Yup.string().matches(
            new RegExp(stateNames.join('|')),
            'State must be selected from the list.'
          ),
        },
        {
          name: 'borrowerZip',
          label: 'Zip',
          type: 'text',
          className: 'mobile-half tablet-half desktop-third',
          validationSchema: Yup.string().matches(
            /^\d{5}(-?\d{4})?$/,
            'Zip must be formatted either XXXXX or XXXXX-XXXX.'
          ),
        },
      ],
      [
        {
          name: 'amountRequested',
          label: 'Amount requested',
          type: 'text',
          className: 'mobile-full tablet-full',
          validationSchema: Yup.string()
            .max(50, 'Amount requested is too long.')
            .matches(
              /^[A-Za-z0-9 $.,]+$/,
              "You've entered a symbol that isn't valid."
            ),
        },
        {
          name: 'dateRequested',
          label: 'Date',
          type: 'date',
          className: 'mobile-full tablet-full',
          minDate: minDate,
          validationSchema: Yup.date(),
        },
      ],
      [
        {
          name: 'loanPurpose',
          label: 'Loan purpose',
          type: 'textarea',
          validationSchema: Yup.string()
            .max(3000, 'Loan purpose is too long.')
            .required('Loan purpose is required.'),
        },
      ],
    ],
  },
];

function CommercialLendingApplication({ error, isLoading }) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();

  const [isSubmitted, setSubmitted] = useState(false);
  const handleFormSubmit = (values, { setSubmitting, setStatus }) => {
    setSubmitting(true);
    const actualValues = { ...values };

    Object.keys(actualValues).forEach(key => {
      if (actualValues[key] === '') {
        actualValues[key] = null;
      }
    });

    if (!actualValues.dateRequested) {
      actualValues.dateRequested = new Date();
    }

    submitCommercialLendingApplication(actualValues)
      .then(data => {
        setSubmitted(true);
        setSubmitting(false);
      })
      .catch(error => {
        setSubmitted(false);
        setSubmitting(false);
        setStatus(error.response.data);
      })
      .finally(() => {
        window.scrollTo(0, 200);
      });

    triggerGTMEvent({
      name: 'vmf.cta_click',
      location: 'Commercial Lending',
      category: 'loan_application',
      action: 'form',
      label: 'inquire_now_form',
    })();
  };

  const { isMobile } = useIsMobile(parseInt(theme.breakpoints.desktop));

  return (
    <Layout isLoading={isLoading}>
      <SEO
        title='Commercial Lending Loan Program Application'
        description='Request a quote today about commercial lending from Vanderbilt Mortgage.'
      />

      <Styles>
        <Heading>
          <img
            src='/images/Vanderbilt_Full_Name_Logo_Wht.png'
            alt='Vanderbilt Full Name Logo'
            width={374}
            style={{ width: 374, maxWidth: '100%', height: 'auto' }}
          />
          <p className='type-p'>
            Let us know a little about yourself and the project you're
            interested in financing through Vanderbilt.
          </p>
        </Heading>

        <FormOverlap
          fields={fields}
          onSubmit={handleFormSubmit}
          isSubmitted={isSubmitted}
          successMessage='Thanks for your submission!  Our team will review your inquiry and be in contact soon.'
          className={createGTMClass('form', 'form', 'button')}
        />

        <CTACardv2
          preHeaderContent={'Have Questions?'}
          headerContent={`We're Here to Help`}
          ctaIntro={
            <>
              <p>
                Contact Kurt Wilkerson & Seth Ray <br />
                <a
                  href='mailto:clt@vmf.com'
                  className={createGTMClass('email', 'prefoot_cta', 'text')}
                >
                  CLT@vmf.com
                </a>
              </p>
            </>
          }
          buttonText={isMobile ? 'Contact Us' : null}
          buttonUrl={isMobile ? 'mailto:clt@vmf.com' : null}
          buttonLinkExternal={true}
        />
      </Styles>
    </Layout>
  );
}

CommercialLendingApplication.getInitialProps = async ({}) => {
  try {
    return;
  } catch (error) {
    return {
      error,
    };
  }
};

CommercialLendingApplication.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default CommercialLendingApplication;
